/**
 * Holds the "New" marker's position for the duration of a visit.
 *
 * All of the thinking lives in `_libs/new-message-divider.ts`; this is the ref that
 * remembers its answer across renders.
 */

import { useRef } from "react";
import {
  type NewDividerLatch,
  resolveNewDividerLatch,
} from "../_libs/new-message-divider";
import type { UnreadContext, UnreadMessage } from "../_libs/unread-boundary";

export function useNewMessageDivider<TMessage extends UnreadMessage>(
  cid: string,
  /** Oldest-first, so the first match is the boundary. */
  messages: readonly TMessage[],
  context: UnreadContext,
  /** `GET /:cid/unread` has answered for this cid and is not revalidating. */
  unreadSettled: boolean,
  enabled: boolean,
): string | null {
  const latched = useRef<NewDividerLatch | null>(null);

  if (enabled) {
    // Writing a ref during render is safe precisely because the rule is idempotent: the
    // same cid, messages and context always latch the same answer, so a double render
    // cannot disagree with itself. An effect instead would paint one frame without the
    // marker.
    latched.current = resolveNewDividerLatch(
      latched.current,
      cid,
      messages,
      context,
      unreadSettled,
    );
  }

  return enabled ? (latched.current?.messageId ?? null) : null;
}
